import { ArrowDown, ChevronRight } from 'lucide-react';
import { FC, ReactNode, useCallback, useState } from 'react';
import { RouterLink } from 'src/common/components/RouterLink';

import { Collapsible, CollapsibleContent, Tooltip } from '@/design-system/components/ui';

interface SideNavItemProps {
  active?: boolean;
  children?: ReactNode;
  depth?: number;
  disabled?: boolean;
  external?: boolean;
  icon?: ReactNode;
  label?: ReactNode;
  open?: boolean;
  path?: string;
  title: string;
  tooltip?: string;
  showTooltip?: boolean;
}

export const SideNavItem: FC<SideNavItemProps> = (props) => {
  const {
    active,
    children,
    depth = 0,
    disabled,
    external,
    icon,
    label,
    open: openProp,
    path,
    title,
    tooltip,
    showTooltip,
  } = props;
  const [open, setOpen] = useState<boolean>(!!openProp);

  const handleToggle = useCallback((): void => {
    setOpen((prevOpen) => !prevOpen);
  }, []);

  // Icons can be defined at top level only, deep levels have bullets instead of actual icons.

  let startIcon: ReactNode;

  if (depth === 0) {
    startIcon = icon;
  } else {
    startIcon = (
      <div
        style={{
          alignItems: 'center',
          display: 'flex',
          height: 20,
          justifyContent: 'center',
          width: 20,
        }}
      >
        <div
          style={{
            backgroundColor: active ? 'var(--nav-item-icon-active-color)' : 'var(--nav-item-icon-color)',
            borderRadius: '50%',
            height: active ? 6 : 4,
            opacity: active ? 1 : 0, // remove this if you want it to be visible
            width: active ? 6 : 4,
          }}
        />
      </div>
    );
  }

  const offset = depth === 0 ? 0 : (depth - 1) * 16;

  const navItemBaseStyle = {
    paddingLeft: `${16 + offset}px`,
    paddingRight: '16px',
    paddingTop: '6px',
    paddingBottom: '6px',
    backgroundColor: active && depth === 0 ? 'var(--nav-item-active-bg)' : undefined,
  };

  // Branch

  if (children) {
    return (
      <li>
        <button
          disabled={disabled}
          onClick={handleToggle}
          className="tw:flex tw:w-full tw:items-center tw:justify-start tw:rounded tw:text-left tw:transition-colors tw:border-none tw:cursor-pointer"
          style={{ ...navItemBaseStyle, font: 'inherit' }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'var(--nav-item-hover-bg)';
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.backgroundColor =
              active && depth === 0 ? 'var(--nav-item-active-bg)' : '';
          }}
        >
          {startIcon && (
            <span
              style={{
                alignItems: 'center',
                color: active ? 'var(--nav-item-icon-active-color)' : 'var(--nav-item-icon-color)',
                display: 'inline-flex',
                justifyContent: 'center',
                marginRight: 16,
              }}
            >
              {startIcon}
            </span>
          )}
          <span
            style={{
              color: active
                ? 'var(--nav-item-active-color)'
                : disabled
                  ? 'var(--nav-item-disabled-color)'
                  : 'var(--nav-item-color)',
              flexGrow: 1,
              fontSize: depth > 0 ? 13 : 14,
              fontWeight: depth > 0 ? 500 : 600,
              lineHeight: '24px',
              whiteSpace: 'nowrap',
            }}
          >
            {title}
          </span>
          {open ? (
            <ArrowDown size={16} className="tw:ml-2" style={{ color: 'var(--nav-item-chevron-color)' }} />
          ) : (
            <ChevronRight size={16} className="tw:ml-2" style={{ color: 'var(--nav-item-chevron-color)' }} />
          )}
        </button>
        <Collapsible open={open} onOpenChange={setOpen}>
          <CollapsibleContent className="tw:mt-0.5">{children}</CollapsibleContent>
        </Collapsible>
      </li>
    );
  }

  // Leaf

  const isExternal = path && external;
  const isInternal = path && !external;

  const itemContent = (
    <>
      {startIcon && (
        <span
          style={{
            alignItems: 'center',
            color: active ? 'var(--nav-item-icon-active-color)' : 'var(--nav-item-icon-color)',
            display: 'inline-flex',
            justifyContent: 'center',
            marginRight: 16,
          }}
        >
          {startIcon}
        </span>
      )}
      <span
        style={{
          color: active
            ? 'var(--nav-item-active-color)'
            : disabled
              ? 'var(--nav-item-disabled-color)'
              : 'var(--nav-item-color)',
          flexGrow: 1,
          fontSize: depth > 0 ? 13 : 14,
          fontWeight: depth > 0 ? 500 : 600,
          lineHeight: '24px',
          whiteSpace: 'nowrap',
        }}
      >
        {title}
      </span>
      {label && <span style={{ marginLeft: 16 }}>{label}</span>}
    </>
  );

  const commonProps = {
    className:
      'tw:flex tw:w-full tw:items-center tw:justify-start tw:rounded tw:text-left tw:transition-colors tw:no-underline tw:border-none tw:cursor-pointer',
    style: { ...navItemBaseStyle, font: 'inherit', color: 'inherit' },
    onMouseEnter: (e: React.MouseEvent<HTMLElement>) => {
      (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--nav-item-hover-bg)';
    },
    onMouseLeave: (e: React.MouseEvent<HTMLElement>) => {
      (e.currentTarget as HTMLElement).style.backgroundColor = active && depth === 0 ? 'var(--nav-item-active-bg)' : '';
    },
    'data-testid': `side-nav-item-${title.toLowerCase().replace(' ', '-')}`,
  };

  return (
    <li>
      <Tooltip content={showTooltip && tooltip}>
        <span>
          {isExternal ? (
            <a href={path} target="_blank" rel="noopener noreferrer" {...commonProps}>
              {itemContent}
            </a>
          ) : isInternal ? (
            <RouterLink href={path} {...commonProps}>
              {itemContent}
            </RouterLink>
          ) : (
            <button disabled={disabled} {...commonProps}>
              {itemContent}
            </button>
          )}
        </span>
      </Tooltip>
    </li>
  );
};
